import type { SerializedStyles } from '@emotion/react'
import {
  CancelIcon,
  CheckedCircleIconIcon,
  GiftIcon,
} from '@twistopayments/design-system/icons'

import type {
  PlanFeature,
  SectionsPlanComponent,
  SectionsPlanFeatureComponent,
} from '@twisto/api-cms'

import { Headline } from '../../article-components'
import { Tooltip } from '../../atoms'

import * as styles from './pricing-table.styles'

type PricingTableProps = {
  features: SectionsPlanFeatureComponent[]
  plans: SectionsPlanComponent[]
  headline: string
  tooltipLabel: string
  tableCSS: SerializedStyles
}

type FeatureRowProps = {
  feature?: PlanFeature
  plans: SectionsPlanComponent[]
  hintMsg?: string
  lastFeatureId?: string | number
}

const PlanHead = (plan: SectionsPlanComponent) => {
  const { name, description, price, gift, extra } = plan

  return (
    <th css={[styles.planHead, extra ? styles.highlighted : '']}>
      {extra && <div css={styles.extra}>{extra}</div>}
      {gift && (
        <div css={styles.gift}>
          <GiftIcon size="24" />
          <div>{gift}</div>
        </div>
      )}
      <h3 css={styles.name}>{name}</h3>
      <div css={styles.priceLabel}>{price}</div>
      <div css={styles.description}>{description}</div>
    </th>
  )
}

const FeatureRow = ({
  feature,
  plans,
  hintMsg,
  lastFeatureId,
}: FeatureRowProps) => {
  if (!feature) {
    return null
  }

  const isLastFeature = feature.id === lastFeatureId

  const { name, headline, text, url } = feature

  return (
    <tr css={[styles.row, isLastFeature]}>
      <td css={styles.featureColumn}>
        <div css={styles.featureName}>
          {name}
          <Tooltip
            content={
              <div css={styles.featureRowTooltip}>
                <h4>{headline}</h4>
                <p>{text}</p>
                <p css={styles.tooltipHint}>{hintMsg}</p>
              </div>
            }
            url={url}
          />
        </div>
      </td>
      {plans.map((plan) => (
        <td
          key={plan.id}
          css={[
            styles.planColumn,
            plan.extra ? styles.highlightedColumn : '',
            plan.extra && isLastFeature ? styles.highlightedColumnLast : '',
          ]}
        >
          {plan.plan_features?.find(
            (planFeature) => planFeature.id === feature.id
          ) ? (
            <div css={styles.checkmarkIcon}>
              <CheckedCircleIconIcon size="24" />
            </div>
          ) : (
            <div>
              <CancelIcon size="24" />
            </div>
          )}
        </td>
      ))}
    </tr>
  )
}

export const PricingTable = ({
  features,
  plans,
  headline,
  tooltipLabel,
  tableCSS,
}: PricingTableProps) => {
  const lastFeatureId = features[features.length - 1]?.feature?.id

  return (
    <table css={[styles.table, tableCSS]}>
      <thead>
        <tr>
          <th css={styles.headline}>
            <Headline name={headline} tag="h3" />
          </th>
          {plans.map((plan) => (
            <PlanHead key={plan.id} {...plan} />
          ))}
        </tr>
      </thead>
      <tbody>
        {features.map(({ id, feature }) => (
          <FeatureRow
            key={id}
            feature={feature}
            hintMsg={tooltipLabel}
            lastFeatureId={lastFeatureId}
            plans={plans}
          />
        ))}
      </tbody>
    </table>
  )
}
